// v3.5.99.7.5 — Feedbucket feedback widget loader.
//
// Injects the Feedbucket script tag into <head> only when the logged-in
// user is an internal Ferrum employee (email ends in @ferrumit.com).
// This keeps the widget out of the way for:
//   - Anonymous/unauthenticated visitors (e.g. the /login page on first hit)
//   - Future external client access (none today; future-proofing)
//   - The PortalUploadPage public route used by clients to upload onboarding
//     docs — that route doesn't require auth and shouldn't surface internal
//     tooling
//
// One-shot per page load. Once the script is appended to <head>, it stays
// for the session — the module-level `scriptInjected` flag prevents
// re-injection across re-renders, hot reloads, or route changes.
//
// Edge case: if an internal user logs out mid-session, the widget remains
// visible until they refresh. At Ferrum's scale (~4 internal users on this
// tool) this is fine. If it becomes annoying, Feedbucket exposes a SDK to
// hide the widget — call it from a logout cleanup. Not worth the code
// today.
//
// To broaden the criterion later (e.g., to all authenticated users, or to
// a specific profiles.role), change the email check below — single line.

import { useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';

const FEEDBUCKET_KEY        = 'djewRWxDJ8m2ctU06HeG';
const FEEDBUCKET_SCRIPT_SRC = 'https://cdn.feedbucket.app/assets/feedbucket.js';
const INTERNAL_EMAIL_DOMAIN = '@ferrumit.com';

// Module-level flag — survives React re-renders and HMR. Reset only on full
// page reload, which is exactly when we want a fresh injection attempt.
let scriptInjected = false;

/**
 * Mount this once at the top of the app (inside AuthProvider so useAuth
 * resolves). Internally watches the auth user — when an @ferrumit.com
 * email becomes the active user, injects the Feedbucket loader script.
 */
export function useFeedbucket() {
  const { user } = useAuth();

  useEffect(() => {
    if (scriptInjected) return;
    const email = user?.email;
    if (!email) return;
    if (!email.toLowerCase().endsWith(INTERNAL_EMAIL_DOMAIN)) return;

    // Matches Feedbucket's published loader pattern verbatim (defer,
    // data-feedbucket attribute, appended to head).
    const s = document.createElement('script');
    s.defer = true;
    s.src = FEEDBUCKET_SCRIPT_SRC;
    s.dataset.feedbucket = FEEDBUCKET_KEY;
    document.head.appendChild(s);
    scriptInjected = true;
  }, [user]);
}
